import React from 'react';

import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ScienceIcon from '@mui/icons-material/Science';
import SettingsIcon from '@mui/icons-material/Settings';
import TimelineIcon from '@mui/icons-material/Timeline';
import TuneIcon from '@mui/icons-material/Tune';
import WbIncandescentIcon from '@mui/icons-material/WbIncandescent';

import i18n from '../utils/i18n';
import waveLengthRange from './waveLengthRange';

const darkSpectrogram = Array.from({ length: waveLengthRange.stepNum }, (_, i) => ({
    waveLength: waveLengthRange.min + i * waveLengthRange.step,
    irradiance: 0,
    pfd: 0
}));

export interface PageConfig {
    title: React.ReactNode;
    itemName?: React.ReactNode;
    page: string;
    description: React.ReactNode;
    url: string;
    emptyMessage?: React.ReactNode;
    apiEndPoint?: string;
    fetchEndPoint?: string;
    autoCompletePlaceholder?: React.ReactNode;
    errorCodes?: string;
    icon: React.ReactNode;
    group?: React.ReactNode;
    initialData?: Record<string, unknown>;
}

export type MainPageConfig = Record<string, PageConfig>;

const commonInitialData = {
    id: null,
    name: '',
    description: '',
    openedDetailedIntensityPopperId: null
};

const mainPageConfig: MainPageConfig = {
    dashboard: {
        title: i18n('dashboard_title'),
        page: '',
        description: i18n('dashboard_description'),
        url: '/',
        icon: <DashboardIcon />,
        group: ''
    },
    projects: {
        title: i18n('project_editor_title'),
        itemName: i18n('project'),
        page: 'projects',
        description: i18n('project_description'),
        url: '/projects',
        emptyMessage: i18n('no_project'),
        apiEndPoint: 'projects',
        fetchEndPoint: 'projects/withLightRecipes',
        errorCodes: 'project',
        autoCompletePlaceholder: i18n('search_for_project'),
        icon: <AssignmentIcon />,
        initialData: {
            ...commonInitialData,
            recipeId: null,
            spectrogramMap: [],
            recipeLightRecipes: [],
            zoneId: 'root'
        }
    },
    zones: {
        title: i18n('zones_title'),
        itemName: i18n('zone'),
        page: 'zones',
        description: i18n('zones_description'),
        url: '/zones',
        emptyMessage: i18n('no_zone'),
        apiEndPoint: 'zones',
        fetchEndPoint: 'zones',
        autoCompletePlaceholder: i18n('search_for_zone'),
        icon: <AccountTreeIcon />,
        group: i18n('zone_device_management'),
        initialData: {
            ...commonInitialData,
            parent: 'root'
        }
    },
    lightChannels: {
        title: i18n('light_channels_title'),
        itemName: i18n('light_channel'),
        page: 'light-channels',
        description: i18n('light_channel_description'),
        url: '/light-channels',
        emptyMessage: i18n('no_light_channel'),
        apiEndPoint: 'lightChannels',
        fetchEndPoint: 'lightChannels/withZones',
        autoCompletePlaceholder: i18n('search_for_light_channel'),
        icon: <WbIncandescentIcon />,
        group: i18n('zone_device_management'),
        initialData: {
            ...commonInitialData,
            resolution: 8,
            sed: 1,
            channel: '',
            isSubmitted: false,
            spectrogram: [],
            zoneId: 'root',
            csvData: [],
            spectrogramRanges: {
                pfd: {},
                irradiance: {}
            }
        }
    },
    spectrumMixers: {
        title: i18n('spectrum_mixer_title'),
        itemName: i18n('spectrum_mix'),
        page: 'spectrum-mixes',
        description: i18n('spectrum_mixer_description'),
        url: '/spectrum-mixes',
        emptyMessage: i18n('no_spectrum_mix'),
        apiEndPoint: 'spectrumMixes',
        fetchEndPoint: 'spectrumMixes/withLightChannels',
        autoCompletePlaceholder: i18n('search_for_spectrum_mix'),
        icon: <TuneIcon />,
        group: i18n('zone_device_management'),
        initialData: {
            ...commonInitialData,
            zoneId: 'root',
            lightChannels: [],
            lightRecipe: null,
            aggregatedSpectrogram: [],
            aggregatedIntensity: null,
            settings: {
                carouselView: false,
                intensityUnit: 'pfd'
            }
        }
    },
    recipes: {
        title: i18n('recipes_title'),
        itemName: i18n('recipe'),
        page: 'recipes',
        icon: <ScienceIcon />,
        description: i18n('recipe_description'),
        url: '/recipes',
        emptyMessage: i18n('no_recipe'),
        apiEndPoint: 'growthRecipes',
        fetchEndPoint: 'growthRecipes',
        autoCompletePlaceholder: i18n('search_for_recipe'),
        group: i18n('zone_manage_projects'),
        initialData: {
            ...commonInitialData,
            sectionList: []
        }
    },
    lightRecipes: {
        title: i18n('light_recipes_title'),
        itemName: i18n('light_recipe'),
        page: 'light-recipes',
        description: i18n('light_recipe_description'),
        url: '/light-recipes',
        emptyMessage: i18n('no_light_recipe'),
        apiEndPoint: 'lightRecipes',
        fetchEndPoint: 'lightRecipes',
        autoCompletePlaceholder: i18n('search_for_light_recipe'),
        icon: <TimelineIcon />,
        group: i18n('zone_manage_projects'),
        initialData: {
            ...commonInitialData,
            csvData: [],
            spectrogramRanges: {
                pfd: {},
                irradiance: {}
            },
            spectrogram: darkSpectrogram
        }
    },
    settings: {
        title: i18n('settings'),
        page: 'settings',
        description: i18n('settings_description'),
        url: '/settings',
        icon: <SettingsIcon />,
        group: i18n('zone_settings')
    }
};

export default mainPageConfig;
